// Orchestrator for /providers/apply.
//   no token   → email capture (StartScreen) → check-your-email
//   ?token=…   → load draft (GET) → sectioned form → submitted confirmation
// Link states: 404 → invalid, 410 → expired or already reviewed.
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { StartScreen } from './StartScreen';
import { ApplicationForm } from './ApplicationForm';
import { getApplication, type ProviderApplication } from '../lib/providerApi';
import { ApiError } from '../lib/api';
import { track } from '../lib/analytics';
import { PrimaryButton } from '../components/ui';

type Phase = 'start' | 'loading' | 'form' | 'invalid' | 'gone' | 'error' | 'done';

export function ProviderApplyApp() {
  const token = new URLSearchParams(window.location.search).get('token');
  const [phase, setPhase] = useState<Phase>(token ? 'loading' : 'start');
  const [application, setApplication] = useState<ProviderApplication | null>(null);

  useEffect(() => {
    document.title = 'Apply to join · Expy Health';
    track('provider_apply_view');
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getApplication(token)
      .then(({ application }) => {
        if (cancelled) return;
        setApplication(application);
        setPhase('form');
        track('provider_apply_form_view');
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) setPhase('invalid');
        else if (e instanceof ApiError && e.status === 410) setPhase('gone');
        else setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (phase === 'start') return <StartScreen />;

  if (phase === 'loading') {
    return (
      <Centered>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--slate)' }}>Loading your application…</div>
      </Centered>
    );
  }

  if (phase === 'form' && token && application) {
    return (
      <ApplicationForm
        token={token}
        initial={application}
        onSubmitted={() => setPhase('done')}
        onGone={() => setPhase('gone')}
      />
    );
  }

  if (phase === 'done') {
    return (
      <Centered>
        <Badge>
          <svg width={30} height={30} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M4.5 12.5l4.5 4.5L19.5 6.5" stroke="var(--teal-deep)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Badge>
        <div style={{ fontSize: 30, fontWeight: 800 }}>Application received.</div>
        <div className="pretty" style={{ fontSize: 16, fontWeight: 500, color: 'var(--slate)', marginTop: 12, lineHeight: 1.55 }}>
          We review every application and verify every license by hand — you'll hear from us by email. There's nothing
          else you need to do right now.
        </div>
      </Centered>
    );
  }

  const isGone = phase === 'gone';
  return (
    <Centered>
      <Badge>
        <svg width={28} height={28} viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx={12} cy={12} r={9} stroke="var(--teal-deep)" strokeWidth={1.8} />
          <path d="M12 7.5V13" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" />
          <circle cx={12} cy={16.2} r={1.1} fill="var(--teal-deep)" />
        </svg>
      </Badge>
      <div style={{ fontSize: 28, fontWeight: 800 }}>
        {isGone ? 'This link is no longer active.' : phase === 'invalid' ? "That link doesn't look right." : "We couldn't load your application."}
      </div>
      <div className="pretty" style={{ fontSize: 16, fontWeight: 500, color: 'var(--slate)', marginTop: 12, lineHeight: 1.55 }}>
        {isGone
          ? 'It has expired or your application is already with our review team. If you think that’s wrong, request a fresh link below.'
          : phase === 'invalid'
            ? 'The link may have been trimmed by your email app. Request a fresh one below — your draft is safe.'
            : 'Something went wrong on our side or with your connection. Try again in a minute, or request a fresh link.'}
      </div>
      <div style={{ marginTop: 26 }}>
        <PrimaryButton onClick={() => (window.location.href = '/providers/apply')}>Request a new link</PrimaryButton>
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 460, textAlign: 'center' }}>{children}</div>
    </div>
  );
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <div style={{ width: 64, height: 64, borderRadius: 18, background: 'var(--teal-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px' }}>
      {children}
    </div>
  );
}
